import React from 'react';
import styled from 'styled-components';
import { themes, utils } from 'kb-shared';
import { generateCalendarLink } from '../../../utilities/appointmentProduct';
import { location as locationIcon, locationDark } from '../../../assets/icons';
import { WhiteButton, YellowButton } from '../../../components/Buttons';

import { Clinic, AppointmentType } from 'kb-shared';

const { colors, fonts } = themes;
const { momentInTimeZone, inFuture } = utils;

type Props = {
  type: AppointmentType;
  startTime: string;
  endTime: string;
  timeZone: string;
  location: Clinic;
  toggleModal: () => void;
  hasStepper?: boolean;
  videoLink?: string;
};

const AppointmentCard = (props: Props) => {
  const {
    type,
    startTime,
    endTime,
    location: { name: locationName, address },
    timeZone,
    toggleModal,
    hasStepper,
    videoLink
  } = props;

  const isFuture = inFuture(endTime);

  const isPhoneCall = type.description.toLowerCase().includes('phone');
  const apptVideoLink = isPhoneCall ? null : videoLink ?? type.videoLink;

  const goToVideoLink = () => {
    if (!apptVideoLink) return;
    window.location.href = apptVideoLink;
  };

  const addToCalendar = () =>
    window.open(
      generateCalendarLink({
        appointmentType: type,
        startTime,
        endTime,
        location: props.location
      })
    );

  // @ts-ignore
  const formatTime = (format: string) => momentInTimeZone(startTime, timeZone).format(format);

  return (
    <CardContainer isFuture={isFuture}>
      <div>
        <AppointmentTime hasStepper={hasStepper}>
          <span>{formatTime('dddd, ')}</span>
          <span>{formatTime('MMMM Do')}</span>
          <span>{formatTime('h:mm A z')}</span>
        </AppointmentTime>
        <AppointmentName>{type.name}</AppointmentName>
      </div>
      <LocationWrapper>
        {apptVideoLink && isFuture && (
          <VideoConsult>
            <ConsultInstructions>
              At your scheduled time, join the consult using the button below
            </ConsultInstructions>
            <YellowButton type="submit" onClick={goToVideoLink}>
              Join video consult
            </YellowButton>
          </VideoConsult>
        )}
        {type.virtual && !apptVideoLink && isFuture && (
          <PhoneConsult>
            <ConsultInstructions>Your provider will call you.</ConsultInstructions>
          </PhoneConsult>
        )}
        {!type.virtual && !apptVideoLink && (
          <>
            <img
              src={isFuture ? locationIcon : locationDark}
              alt={'icon'}
              style={{ marginRight: 16 }}
            />
            <AppointmentLocation>
              <ClinicName>{locationName}</ClinicName>
              <Address>{address}</Address>
            </AppointmentLocation>
          </>
        )}
      </LocationWrapper>

      {isFuture && (
        <FutureActionsWrapper>
          <WhiteButton onClick={addToCalendar}>Add to Calendar</WhiteButton>

          <CancelCTAText onClick={toggleModal}>Cancel/Reschedule</CancelCTAText>
        </FutureActionsWrapper>
      )}
    </CardContainer>
  );
};

export default AppointmentCard;

interface ContainerProps {
  isFuture?: boolean;
}
const CardContainer = styled.div`
  padding: 20px;
  box-shadow: 0 5px 12px 0 rgba(0, 0, 0, 0.2);
  position: relative;
  height: 100%;
  z-index: 1;

  ${({ isFuture }: ContainerProps) => `
    background-color: ${isFuture ? 'white' : colors.KBGrey};
    `}
`;

interface AppointmentTimeProps {
  hasStepper?: boolean;
}

const AppointmentTime = styled.div`
  ${fonts.DomaineDisplay};
  font-size: 36px;
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  margin-top: ${({ hasStepper }: AppointmentTimeProps) => (hasStepper ? '35px' : '0px')};
`;

const AppointmentLocation = styled.div`
  ${fonts.FoundersGrotesk};
  font-size: 16px;
  display: flex;
  flex-direction: column;
`;

const ClinicName = styled.span`
  font-weight: bold;
`;

const Address = styled.span``;

const LocationWrapper = styled.div`
  display: flex;
  margin: 28px 0;
`;

const CancelCTAText = styled.a`
  display: flex;
  cursor: pointer;
  ${fonts.FoundersGrotesk};
  text-decoration: underline;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin: 28px auto 0;
  font-size: 16px;
  :hover {
    font-weight: bold;
  }
`;

const ConsultInstructions = styled('div')`
  max-width: 270px;
  text-align: center;
  ${fonts.FoundersGrotesk}
  font-size: 14px;
  font-weight: 300;
  color: ${colors.KBNight};
  margin-bottom: 12px;
`;

const VideoConsult = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 135px;
  width: 100%;
  padding-left: 9px;
  padding-right: 9px;
  background: ${colors.KBPaleYellow};
`;

const PhoneConsult = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 9px;
  padding-top: 20px;
  background: ${colors.KBPaleYellow};
`;

const FutureActionsWrapper = styled.div`
  align-self: flex-end;
  align-items: center;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
`;

export const AppointmentName = styled.span`
  ${fonts.FoundersGrotesk}
  font-size: 14px;
  background-color: ${colors.KBTwilightLight};
  padding: 4px 12px;
  text-transform: uppercase;
  color: ${colors.KBWhite};
  margin-top: 20px;
`;
